import { useMemo } from 'react';
import { SortedTableRow } from '@/features/ai-agents/chat/MarkdownTableVisualization/types';
import {
  createChartData,
  createCompareChartSeries,
  createCompareLineChartData,
  createVariantChartSeries,
  excludeTotalRows,
  filterCompareCurrentRows,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/utils';

interface UseChartDataOptions {
  breakdownColumnIndex: number;
  compareColumnIndex: number;
  isCompareReport: boolean;
  isVariantReport: boolean;
  selectedXColumnIndex: number;
  selectedYColumnIndex: number;
  sortedRows: SortedTableRow[];
  variantColumnIndex: number;
}

export function useChartData({
  breakdownColumnIndex,
  compareColumnIndex,
  isCompareReport,
  isVariantReport,
  selectedXColumnIndex,
  selectedYColumnIndex,
  sortedRows,
  variantColumnIndex,
}: UseChartDataOptions) {
  const currentRows = useMemo(
    () =>
      isCompareReport
        ? filterCompareCurrentRows(
            sortedRows.map((row) => row.values),
            compareColumnIndex,
          )
        : sortedRows.map((row) => row.values),
    [sortedRows, isCompareReport, compareColumnIndex],
  );
  const chartData = useMemo(
    () =>
      createChartData(currentRows, selectedXColumnIndex, selectedYColumnIndex),
    [currentRows, selectedXColumnIndex, selectedYColumnIndex],
  );
  const variantSeries = useMemo(
    () =>
      isVariantReport
        ? createVariantChartSeries(
            sortedRows.map((row) => row.values),
            variantColumnIndex,
            selectedXColumnIndex,
            selectedYColumnIndex,
          )
        : [],
    [
      isVariantReport,
      sortedRows,
      variantColumnIndex,
      selectedXColumnIndex,
      selectedYColumnIndex,
    ],
  );
  const breakdownSeries = useMemo(
    () =>
      breakdownColumnIndex >= 0
        ? createVariantChartSeries(
            sortedRows.map((row) => row.values),
            breakdownColumnIndex,
            selectedXColumnIndex,
            selectedYColumnIndex,
          )
        : [],
    [
      breakdownColumnIndex,
      sortedRows,
      selectedXColumnIndex,
      selectedYColumnIndex,
    ],
  );
  const series = breakdownSeries.length > 0 ? breakdownSeries : variantSeries;
  const compareSeries = useMemo(
    () =>
      isCompareReport
        ? createCompareChartSeries(
            sortedRows.map((row) => row.values),
            compareColumnIndex,
            selectedXColumnIndex,
            selectedYColumnIndex,
          )
        : null,
    [
      isCompareReport,
      sortedRows,
      compareColumnIndex,
      selectedXColumnIndex,
      selectedYColumnIndex,
    ],
  );
  const compareGroupedData = useMemo(
    () =>
      compareSeries
        ? createCompareLineChartData(
            {
              ...compareSeries.current,
              data: excludeTotalRows(compareSeries.current.data),
            },
            compareSeries.compare && {
              ...compareSeries.compare,
              data: excludeTotalRows(compareSeries.compare.data),
            },
          )
        : null,
    [compareSeries],
  );
  const comparePieData = useMemo(() => {
    if (!compareSeries?.compare) {
      return null;
    }

    return {
      compare: compareSeries.compare.data,
      compareLabel: compareSeries.compare.name,
      current: compareSeries.current.data,
    };
  }, [compareSeries]);

  return { chartData, compareGroupedData, comparePieData, series };
}
